import { useQueryClient } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { getBasePaySchedulesQuery } from 'components/hooks/payroll/utils'
import { useGetSamplePayPeriods } from 'components/hooks/useGetSamplePayPeriods'
import { useUpdatePaySchedule } from 'components/hooks/useUpdatePaySchedule'
import { Button } from 'slab'
import { type PaySchedule, type PayScheduleFormInputs } from 'types/payroll'

import { BankClosureStrategyInput } from './BankClosureStrategyInput'
import { DeactivatePayScheduleButton } from './DeactivatePayScheduleButton'
import { PayFrequencyField, PayScheduleNameField } from './FormFields'
import { PayDateFields } from './PayDateFields'
import { PayDatePreview } from './PayDatePreview'

interface PayScheduleDetailsFormProps {
  paySchedule: PaySchedule
  onSuccess: (scheduleId: string) => void
  onDiscard: () => void
}

export const PayScheduleDetailsForm = ({ paySchedule, onSuccess, onDiscard }: PayScheduleDetailsFormProps) => {
  const queryClient = useQueryClient()
  const updatePayScheduleMutation = useUpdatePaySchedule()

  const {
    control,
    getValues,
    formState: { isSubmitting, errors, isValid },
    handleSubmit,
    register,
    watch,
    trigger
  } = useForm<PayScheduleFormInputs>({
    mode: 'all',
    defaultValues: {
      frequency: paySchedule.frequency,
      name: paySchedule.name,
      bankClosureStrategy: paySchedule.bank_closure_strategy,
      firstDay: paySchedule.first_day,
      secondDay: paySchedule.second_day,
      examplePayDate: paySchedule.reference_pay_date,
      endDate: paySchedule.end_date || null,
    }
  })

  const onSubmit = (data: PayScheduleFormInputs) => {
    if (isValid) {
      updatePayScheduleMutation.mutate({
        id: paySchedule.id,
        frequency: data.frequency,
        name: data.name,
        bank_closure_strategy: data.bankClosureStrategy,
        first_day: data.firstDay,
        second_day: data.secondDay,
        reference_pay_date: data.examplePayDate,
        end_date: data.endDate || null,
        is_verified: true
      }, {
        onSuccess: () => {
          onSuccess(paySchedule.id)
        }
      })
    }
  }

  const frequency = watch('frequency')

  const { data, isLoading, error } = useGetSamplePayPeriods({
    frequency,
    bank_closure_strategy: watch('bankClosureStrategy'),
    first_day: watch('firstDay'),
    second_day: watch('secondDay'),
    reference_pay_date: watch('examplePayDate'),
    isValid,
  })
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='grid grid-cols-2 gap-4 p-6 border-b border-gray-200'>
        <PayFrequencyField
          control={control}
          error={errors.frequency}
          fieldId='frequency'
          disabled={paySchedule.is_verified}
        />

        <PayScheduleNameField
          control={control}
          error={errors.name}
          fieldId='name'
        />

        <PayDateFields
          frequency={frequency}
          control={control}
          register={register}
          errors={errors}
          watch={watch}
          trigger={trigger}
          getValues={getValues}
        />

        <div className='col-span-2 '>
          <div className='grid grid-cols-3 gap-6'>
            <BankClosureStrategyInput
              register={register}
              error={errors.bankClosureStrategy}
            />

            {isValid && (
              <div className='col-span-3 lg:col-span-1'>
                <PayDatePreview isLoading={isLoading} data={data} error={error?.message} />
              </div>
            )}
          </div>
        </div>

      </div>
      <div className='flex flex-wrap justify-end items-center gap-2 p-6'>
        <DeactivatePayScheduleButton
          paySchedule={paySchedule}
          onSuccess={() => {
            onSuccess(paySchedule.id)
            queryClient.invalidateQueries({ queryKey: getBasePaySchedulesQuery() })
          }}
        />
        <div className='flex flex-wrap gap-2 justify-end'>
          <Button variant='secondary' leftIcon={X} onClick={onDiscard}>Discard changes</Button>
          <Button
            variant='primary'
            disabled={isSubmitting || !isValid}
            onClick={handleSubmit(onSubmit)}
            leftIcon={Check}
          >
            {paySchedule.is_verified ? 'Save changes' : 'Confirm and save changes'}
          </Button>
        </div>
      </div>
    </form>
  )
}
